/**
 * Offline queue for form submissions and other write operations.
 * Stores pending requests in localStorage and flushes them when back online.
 *
 * Why not IndexedDB? For MVP, localStorage (5-10MB) is sufficient for form payloads.
 * We can migrate to IndexedDB later for larger binary blobs (images).
 */

import { toast } from 'sonner';

const STORAGE_KEY = 'savenes_offline_queue';
const MAX_ITEMS = 200;

const isOffline = () => typeof navigator !== 'undefined' && navigator.onLine === false;

// ---------------- Storage primitives ----------------
const readQueue = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const writeQueue = (items) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    // Fire a custom event so UI components can refresh their badge counts
    window.dispatchEvent(new CustomEvent('offline-queue-changed', { detail: { count: items.length } }));
  } catch (err) {
    console.error('Failed to persist offline queue:', err);
  }
};

// ---------------- Public API ----------------

export const enqueue = (item) => {
  const items = readQueue();
  if (items.length >= MAX_ITEMS) {
    throw new Error('Offline queue is full. Please sync before adding more entries.');
  }
  const entry = {
    id: `q_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    createdAt: new Date().toISOString(),
    retryCount: 0,
    ...item, // { type, method, url, body, meta }
  };
  items.push(entry);
  writeQueue(items);
  return entry;
};

export const getQueue = () => readQueue();

export const getQueueCount = () => readQueue().length;

export const removeFromQueue = (id) => {
  const items = readQueue().filter((i) => i.id !== id);
  writeQueue(items);
};

export const clearQueue = () => writeQueue([]);

/**
 * Flush the queue by replaying each request. Called automatically on
 * online/reconnect events, and can be invoked manually from the UI.
 *
 * @param {object} api - The axios instance to use for replaying requests.
 * @returns {Promise<{ succeeded: number, failed: number }>}
 */
export const flushQueue = async (api) => {
  if (isOffline()) return { succeeded: 0, failed: 0, skipped: true };
  const items = readQueue();
  if (items.length === 0) return { succeeded: 0, failed: 0 };

  let succeeded = 0;
  let failed = 0;
  const remaining = [];

  for (const item of items) {
    try {
      const method = (item.method || 'post').toLowerCase();
      if (method === 'post') await api.post(item.url, item.body);
      else if (method === 'put') await api.put(item.url, item.body);
      else if (method === 'patch') await api.patch(item.url, item.body);
      else if (method === 'delete') await api.delete(item.url);
      else await api.request({ method, url: item.url, data: item.body });
      succeeded += 1;
    } catch (err) {
      item.retryCount = (item.retryCount || 0) + 1;
      item.lastError = err?.response?.data?.detail || err?.message || 'Unknown';
      // Drop after 5 failed attempts (preserves entry in logs for user to see)
      if (item.retryCount < 5) {
        remaining.push(item);
      } else {
        failed += 1;
      }
    }
  }

  writeQueue(remaining);

  if (succeeded > 0) {
    toast.success(`Synced ${succeeded} offline ${succeeded === 1 ? 'entry' : 'entries'}`);
  }
  if (failed > 0) {
    toast.error(`${failed} offline entries could not be synced after multiple attempts. Review them in the offline panel.`);
  }

  return { succeeded, failed };
};

/**
 * Helper for form submissions. Tries online first; falls back to queue.
 */
export const submitFormResilient = async (api, formId, payload) => {
  if (!isOffline()) {
    try {
      return await api.post(`/forms/${formId}/submit`, payload);
    } catch (err) {
      // If the failure looks like network (no response), queue it; otherwise bubble
      if (err.response) throw err;
    }
  }
  // Offline path — queue for later
  const entry = enqueue({
    type: 'form_submission',
    method: 'post',
    url: `/forms/${formId}/submit`,
    body: payload,
    meta: { formId, patientId: payload.patient_id, formName: payload.form_name },
  });
  toast.info('Saved offline — will sync automatically when you reconnect');
  return { data: { offline: true, queued_id: entry.id }, status: 202 };
};

/**
 * Generic helper for any write. Tries online first; on pure network failure (no response),
 * queues it. On HTTP errors (4xx/5xx), it bubbles the error so the UI can handle validation.
 *
 * @param {object}  api            axios instance (with interceptors)
 * @param {object}  spec           { method, url, body }
 * @param {object}  opts           { type, meta, infoMsg }
 */
export const writeResilient = async (api, spec, opts = {}) => {
  const { method = 'post', url, body } = spec;
  const { type = 'write', meta = {}, infoMsg = 'Saved offline — will sync when you reconnect' } = opts;

  if (!isOffline()) {
    try {
      const m = method.toLowerCase();
      if (m === 'post') return await api.post(url, body);
      if (m === 'put') return await api.put(url, body);
      if (m === 'patch') return await api.patch(url, body);
      if (m === 'delete') return await api.delete(url);
      return await api.request({ method: m, url, data: body });
    } catch (err) {
      if (err.response) throw err;
    }
  }
  const entry = enqueue({ type, method, url, body, meta });
  toast.info(infoMsg);
  return { data: { offline: true, queued_id: entry.id }, status: 202 };
};

// ---------------- Domain-specific helpers ----------------

/** Create a task (with offline fallback). Pass the same payload you'd send to POST /tasks. */
export const createTaskResilient = (api, payload) =>
  writeResilient(api, { method: 'post', url: '/tasks', body: payload }, {
    type: 'task_create',
    meta: { title: payload.title, patientId: payload.patient_id },
    infoMsg: 'Task saved offline — will sync when you reconnect',
  });

/** Update a task's status/fields (with offline fallback). */
export const updateTaskResilient = (api, taskId, updates) =>
  writeResilient(api, { method: 'put', url: `/tasks/${taskId}`, body: updates }, {
    type: 'task_update',
    meta: { taskId, updates },
    infoMsg: 'Task update saved offline — will sync when you reconnect',
  });

/** Create a clinical note (with offline fallback). */
export const createNoteResilient = (api, payload) =>
  writeResilient(api, { method: 'post', url: '/notes', body: payload }, {
    type: 'note_create',
    meta: { encounterId: payload.encounter_id, patientId: payload.patient_id },
    infoMsg: 'Note saved offline — will sync when you reconnect',
  });

/** Create an appointment (with offline fallback). */
export const createAppointmentResilient = (api, payload) =>
  writeResilient(api, { method: 'post', url: '/appointments', body: payload }, {
    type: 'appointment_create',
    meta: { patientId: payload.patient_id, providerId: payload.provider_id, scheduledDate: payload.scheduled_date },
    infoMsg: 'Appointment saved offline — will sync when you reconnect',
  });
